import { useEffect, useRef, useState } from 'react';
import { COURSE, STRING_NAMES, TABS } from '@gc/shared';
import { audio, pluck } from '../lib/audio.js';
import { haptic } from '../lib/telegram.js';

/**
 * Проигрыватель табулатур: колонка — одна нота, строка — струна. Во время
 * игры подсвечивается текущая колонка, так что видно, где ты находишься,
 * даже если сам ещё не попадаешь в темп.
 */
export function TabPlayer({ initial }: { initial?: string }): JSX.Element {
  const [key, setKey] = useState(initial && TABS[initial] ? initial : 'ode');
  const piece = TABS[key]!;

  const [bpm, setBpm] = useState(piece.bpm);
  const [loop, setLoop] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(-1);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    setBpm(TABS[key]!.bpm);
  }, [key]);

  function stop(): void {
    timers.current.forEach(window.clearTimeout);
    timers.current = [];
    setPlaying(false);
    setAt(-1);
  }

  useEffect(() => stop, []);

  function play(): void {
    const c = audio();
    haptic.tap();
    stop();
    setPlaying(true);

    const step = 60 / bpm;
    const rounds = loop ? 8 : 1;
    // Пауза в одну долю между повторами: иначе круг слипается в одну фразу.
    const cycle = piece.notes.length + 1;
    const t0 = c.currentTime + 0.1;

    piece.notes.forEach((note, i) => {
      for (let r = 0; r < rounds; r++) {
        const when = t0 + (r * cycle + i) * step;
        pluck(note[0], note[1], when, 0.55);
        timers.current.push(
          window.setTimeout(() => setAt(i), Math.max(0, (when - c.currentTime) * 1000)),
        );
      }
    });

    timers.current.push(window.setTimeout(stop, rounds * cycle * step * 1000));
  }

  const current = at >= 0 ? piece.notes[at] : undefined;

  return (
    <div className="card">
      <div className="chips">
        {Object.entries(TABS).map(([k, t]) => (
          <button
            key={k}
            type="button"
            className="chip"
            aria-pressed={k === key}
            onClick={() => {
              stop();
              setKey(k);
            }}
          >
            {t.name}
          </button>
        ))}
      </div>

      <p className="hint">{COURSE.tabs[piece.courseKey]?.how}</p>

      <div className="tabgrid">
        <table>
          <tbody>
            {[5, 4, 3, 2, 1, 0].map((row) => (
              <tr key={row}>
                <th>{STRING_NAMES[row]!.note}</th>
                {piece.notes.map((note, i) => {
                  const hit = note[0] === row;
                  return (
                    <td key={i} className={`${hit ? 'hit' : ''} ${hit && at === i ? 'now' : ''}`}>
                      {hit ? <span>{note[1]}</span> : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row">
        <span className="field">Темп</span>
        <div className="tempo">
          <input
            type="range"
            min={40}
            max={140}
            value={bpm}
            onChange={(e) => setBpm(Number(e.target.value))}
          />
          <span className="mono">{bpm} bpm</span>
        </div>
      </div>

      <div className="row buttons">
        <button type="button" className="btn" onClick={playing ? stop : play}>
          {playing ? '■ Стоп' : '▶ Играть'}
        </button>
        <button
          type="button"
          className="chip ghost"
          aria-pressed={loop}
          onClick={() => setLoop((v) => !v)}
        >
          ↻ По кругу
        </button>
        <span className="mono dim">
          {current ? `${STRING_NAMES[current[0]]!.label} · ${current[1] === 0 ? 'открытая' : `${current[1]} лад`}` : ''}
        </span>
      </div>
    </div>
  );
}
